import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { Activity, Archive, ClipboardList, LockKeyhole, LogOut, MessageSquare, RefreshCcw, Search, Settings, ShieldCheck, Store, Webhook } from "lucide-react";
import "./styles.css";

type Page = "dashboard" | "merchants" | "subscriptions" | "exports" | "sync" | "pcd" | "webhooks" | "activity" | "signals" | "features" | "settings";
const pages: Array<{ key: Page; label: string; icon: React.ComponentType<{ size?: number }> }> = [
  { key: "dashboard", label: "Dashboard", icon: Activity },
  { key: "merchants", label: "Merchants", icon: Store },
  { key: "subscriptions", label: "Subscriptions", icon: ShieldCheck },
  { key: "exports", label: "Export Health", icon: Archive },
  { key: "sync", label: "Sync Jobs", icon: RefreshCcw },
  { key: "pcd", label: "PCD Status", icon: LockKeyhole },
  { key: "webhooks", label: "Webhooks", icon: Webhook },
  { key: "activity", label: "Activity", icon: ClipboardList },
  { key: "signals", label: "App Review Signals", icon: Search },
  { key: "features", label: "Features", icon: MessageSquare },
  { key: "settings", label: "Settings", icon: Settings }
];

function App() {
  const [session, setSession] = useState<boolean | null>(null);
  const [page, setPage] = useState<Page>("dashboard");
  useEffect(() => {
    void api<{ ok: boolean }>("/api/session").then((data) => setSession(data.ok)).catch(() => setSession(false));
  }, []);
  if (session === null) return <main className="center">Loading...</main>;
  if (!session) return <Login onLogin={() => setSession(true)} />;
  return (
    <div className="admin-shell">
      <aside>
        <div className="brand"><span>CT</span><strong>Custory Admin</strong></div>
        {pages.map((item) => {
          const Icon = item.icon;
          return <button key={item.key} className={page === item.key ? "active" : ""} onClick={() => setPage(item.key)}><Icon size={17} />{item.label}</button>;
        })}
        <button onClick={async () => { await api("/api/logout", { method: "POST" }); setSession(false); }}><LogOut size={17} />Logout</button>
      </aside>
      <main>
        <header>
          <h1>{pages.find((item) => item.key === page)?.label}</h1>
          <p>Operational metadata only. Customer names, emails, notes, order details, return details, and export content are never shown here.</p>
        </header>
        {page === "dashboard" ? <Overview /> : null}
        {page === "merchants" ? <Table endpoint="/api/shops" /> : null}
        {page === "subscriptions" ? <Table endpoint="/api/shops" /> : null}
        {page === "exports" ? <Table endpoint="/api/export-health" /> : null}
        {page === "sync" ? <Table endpoint="/api/sync-jobs" /> : null}
        {page === "pcd" ? <Table endpoint="/api/pcd" /> : null}
        {page === "webhooks" ? <Table endpoint="/api/webhooks" /> : null}
        {page === "activity" ? <Table endpoint="/api/activity" /> : null}
        {page === "signals" ? <Table endpoint="/api/review-signals" /> : null}
        {page === "features" ? <Features /> : null}
        {page === "settings" ? <SettingsPanel /> : null}
      </main>
    </div>
  );
}

function Login({ onLogin }: { onLogin: () => void }) {
  const [username, setUsername] = useState("owner");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  return (
    <main className="login">
      <section>
        <h1>Custory Owner Admin</h1>
        <p>Sign in to view operational metadata. This admin never exposes customer timeline content.</p>
        <input value={username} onChange={(event) => setUsername(event.target.value)} aria-label="Username" />
        <input value={password} onChange={(event) => setPassword(event.target.value)} aria-label="Password" type="password" />
        <button onClick={async () => {
          try {
            await api("/api/login", { method: "POST", body: JSON.stringify({ username, password }), headers: { "content-type": "application/json" } });
            onLogin();
          } catch {
            setError("Invalid owner login.");
          }
        }}>Login</button>
        {error ? <p className="error">{error}</p> : null}
      </section>
    </main>
  );
}

function Overview() {
  const [overview, setOverview] = useState<Record<string, unknown>>({});
  useEffect(() => {
    void api<{ overview: Record<string, unknown> }>("/api/overview").then((data) => setOverview(data.overview));
  }, []);
  return <section className="metrics">{Object.entries(overview).map(([key, value]) => <article key={key}><span>{key}</span><strong>{String(value)}</strong></article>)}</section>;
}

function Table({ endpoint }: { endpoint: string }) {
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  useEffect(() => {
    void api<Record<string, Array<Record<string, unknown>>>>(endpoint).then((data) => setRows(data.rows || data.shops || []));
  }, [endpoint]);
  const keys = Array.from(new Set(rows.flatMap((row) => Object.keys(row)))).slice(0, 12);
  return (
    <section className="panel">
      <div className="table-wrap">
        <table>
          <thead><tr>{keys.map((key) => <th key={key}>{key}</th>)}</tr></thead>
          <tbody>{rows.length ? rows.map((row, index) => <tr key={index}>{keys.map((key) => <td key={key}>{String(row[key] ?? "")}</td>)}</tr>) : <tr><td>No metadata rows yet.</td></tr>}</tbody>
        </table>
      </div>
    </section>
  );
}

function Features() {
  return <section className="panel"><h2>Feature gates</h2><ul><li>PCD pending and approved states.</li><li>Billing inactive and active locks.</li><li>Return data unavailable state.</li><li>Export cleanup and retention metadata.</li><li>Review signal evidence tracking.</li></ul></section>;
}

function SettingsPanel() {
  return <section className="panel"><h2>Admin safety settings</h2><p>Allowed host guard, signed HttpOnly owner cookie, D1 metadata-only queries, no customer PCD fields.</p></section>;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, init);
  const data = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(data.error || "Request failed");
  return data as T;
}

createRoot(document.getElementById("root")!).render(<App />);
